import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, CheckCheck } from "lucide-react";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const SLIDES = [
  {
    initials: "EP",
    sender: "Emirates Prestige Motors",
    message: (
      <>
        Hi Ahmed, your vehicle is ready for collection at{" "}
        <span className="font-semibold">Emirates Prestige Motors</span>! Our
        showroom is open until 9pm. Reply to confirm your collection time. 🚗
      </>
    ),
    reply: "Perfect, I'll come by at 7pm today. Thanks!",
  },
  {
    initials: "PC",
    sender: "Paws & Care Clinic",
    message: (
      <>
        Hi Sarah, your pet <span className="font-semibold">Bella</span> has
        completed her grooming session at{" "}
        <span className="font-semibold">Paws & Care Clinic</span> and is ready
        for collection! We're open until 8pm. 🐾
      </>
    ),
    reply: "Amazing! On my way now, thank you!",
  },
  {
    initials: "RS",
    sender: "Royal Stitch Tailors",
    message: (
      <>
        Hi Mohammed, your alterations are complete and ready for collection at{" "}
        <span className="font-semibold">Royal Stitch Tailors</span>. We're open
        until 9pm. Reply to confirm. 🧵
      </>
    ),
    reply: "Perfect, I'll pick it up tomorrow morning.",
  },
  {
    initials: "FC",
    sender: "Fresh Clean Laundry",
    message: (
      <>
        Hi Fatima, your laundry order is fresh, pressed, and ready for
        collection at{" "}
        <span className="font-semibold">Fresh Clean Laundry</span>! Open until
        10pm. 👕
      </>
    ),
    reply: "Great, I'll be there by 6pm. Thanks!",
  },
];

const WhatsAppMockup = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % SLIDES.length);
    }, 4000);
    return () => clearInterval(id);
  }, []);

  const slide = SLIDES[index];

  return (
    <div
      data-testid="hero-whatsapp-bubble"
      className="relative w-full max-w-md mx-auto"
    >
      {/* Phone frame */}
      <div className="relative bg-[#0b141a] rounded-[2.5rem] p-3 shadow-2xl shadow-[#7B5EA7]/30 border border-white/10">
        {/* Top status bar */}
        <div className="flex items-center justify-between px-4 pt-2 pb-3 text-white/80 text-[10px] font-medium">
          <span>9:41</span>
          <div className="flex items-center gap-1">
            <span className="w-1 h-1 rounded-full bg-white/80" />
            <span className="w-1 h-1 rounded-full bg-white/80" />
            <span className="w-1 h-1 rounded-full bg-white/80" />
          </div>
        </div>

        {/* Carousel slides */}
        <div className="relative overflow-hidden rounded-2xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
            >
              {/* Chat header */}
              <div className="bg-[#202c33] rounded-t-2xl px-4 py-3 flex items-center gap-3 border-b border-white/5">
                <div className="w-9 h-9 rounded-full ping-gradient flex items-center justify-center text-white font-bold text-sm">
                  {slide.initials}
                </div>
                <div className="flex-1">
                  <div
                    data-testid="hero-mockup-sender"
                    className="text-white text-sm font-semibold"
                  >
                    {slide.sender}
                  </div>
                  <div className="text-emerald-400 text-[10px] font-medium flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
                    online
                  </div>
                </div>
              </div>

              {/* Chat body */}
              <div
                className="px-4 py-5 min-h-[280px] flex flex-col gap-3 rounded-b-2xl"
                style={{
                  backgroundImage:
                    "linear-gradient(rgba(11,20,26,0.92), rgba(11,20,26,0.92)), repeating-linear-gradient(45deg, rgba(255,255,255,0.02) 0 2px, transparent 2px 8px)",
                  backgroundColor: "#0b141a",
                }}
              >
                {/* Incoming business message */}
                <div className="self-start bg-white rounded-2xl rounded-tl-sm px-4 py-2.5 shadow-md max-w-[88%] relative wa-tail">
                  <p className="text-[13.5px] text-gray-900 leading-snug">
                    {slide.message}
                  </p>
                  <div className="flex items-center justify-end gap-1 mt-1">
                    <span className="text-[10px] text-gray-500">9:41</span>
                    <CheckCheck size={13} className="text-[#4B9FE8]" />
                  </div>
                </div>

                {/* Customer reply */}
                <div className="self-end bg-[#005c4b] rounded-2xl rounded-tr-sm px-4 py-2.5 shadow-md max-w-[80%]">
                  <p className="text-[13.5px] text-white leading-snug">
                    {slide.reply}
                  </p>
                  <div className="flex items-center justify-end gap-1 mt-1">
                    <span className="text-[10px] text-white/70">9:42</span>
                    <CheckCheck size={13} className="text-white/80" />
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Dot indicators */}
        <div
          data-testid="hero-mockup-dots"
          className="flex items-center justify-center gap-1.5 pt-4 pb-1"
        >
          {SLIDES.map((_, i) => (
            <button
              key={i}
              data-testid={`hero-mockup-dot-${i}`}
              onClick={() => setIndex(i)}
              aria-label={`Show slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === index
                  ? "w-6 ping-gradient"
                  : "w-1.5 bg-white/25 hover:bg-white/45"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Glow orbs */}
      <div className="orb w-72 h-72 bg-[#4B9FE8] -top-10 -left-10 -z-10" />
      <div className="orb w-72 h-72 bg-[#7B5EA7] -bottom-10 -right-10 -z-10" />
    </div>
  );
};

export const Hero = () => {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative bg-[#1A1A2E] text-white pt-32 sm:pt-36 lg:pt-40 pb-20 lg:pb-28 overflow-hidden"
    >
      {/* Background grid + orbs */}
      <div className="absolute inset-0 bg-grid-dark opacity-60 pointer-events-none" />
      <div className="orb w-[480px] h-[480px] bg-[#4B9FE8] top-[-120px] left-[-120px]" />
      <div className="orb w-[520px] h-[520px] bg-[#7B5EA7] bottom-[-160px] right-[-120px]" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          {/* Left: Copy */}
          <div className="lg:col-span-7 max-w-2xl">
            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="text-[2rem] leading-[1.12] sm:text-[2.75rem] sm:leading-[1.08] lg:text-[3.5rem] lg:leading-[1.05] font-black tracking-tight"
            >
              The <span className="ping-gradient-text">AI-powered</span>{" "}
              WhatsApp platform for UAE service businesses. One tap. Customer
              notified. Job done.
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-9 flex flex-col sm:flex-row gap-3.5"
            >
              <button
                data-testid="hero-cta-products"
                onClick={() => scrollTo("#products")}
                className="ping-gradient text-white font-semibold px-7 py-3.5 rounded-full hover:shadow-xl hover:shadow-[#7B5EA7]/40 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 group"
              >
                See Our Products
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-0.5 transition-transform"
                />
              </button>
              <button
                data-testid="hero-cta-contact"
                onClick={() => scrollTo("#contact")}
                className="border border-white/25 hover:border-white/60 text-white font-semibold px-7 py-3.5 rounded-full hover:bg-white/5 transition-all"
              >
                Get in Touch
              </button>
            </motion.div>
          </div>

          {/* Right: Visual */}
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            >
              <WhatsAppMockup />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
